import { useState, useEffect } from 'react';
import '../IpPatientDetails.css';
import {
    Box, TextField, Button, Stack, Grid, Autocomplete,
    Dialog, Tooltip, Typography, IconButton, InputAdornment,
    Menu, MenuItem,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import {
    CommonPatientDetails, CommonPatientCard, ConsultantPatientCard,
    DeletePopup, CommonPopUp, CommonEditButton, CommonDeleteButton
} from '../../../../../Components/Common Components/CommonComponents';
import CommonValidation from '../../../../../Components/CommonFunctions/CommonValidation';
import { ImagePaths } from '../../../../../Utility/ImagePaths';
import { Colors } from '../../../../../Styles/Colors';
import ToastMsg from '../../../../../Components/ToastMsg/ToastMsg';
import RestAPIService from '../../../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../../../Utility/API/Serviceurls';
import { getCachevalue, localGetItem } from '../../../../../Utility/Services/CacheProviderService';
import { convertDateTime, CheckAccessFunc } from '../../../../../Components/CommonFunctions/CommonFunctions';
import Loader from '../../../../../Components/Loader';
import { DateTime } from 'luxon';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const VentilationFlowSheet = (props) => {
    const { t } = useTranslation();

    const [patientData, setPatientData] = useState({});
    const [attenderData, setAttenderData] = useState({});
    const [withOutAppointment, setWithOutAppointment] = useState({});
    const [clinicId, setClinicId] = useState(null);
    const [NurseList, setNurseList] = useState([]);
    const [nurseName, setNurseName] = useState(null);
    const [ventilationDate, setVentilationDate] = useState(new Date());
    const [DateOpen, setDateOpen] = useState(false);
    const [openPopup, setOpenPopup] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [ventilationData, setVentilationData] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isLoader, setIsLoader] = useState(false);
    const [isErrorMsg, setIsErrorMsg] = useState(false);
    const [isErrorMsgText, setIsErrorMsgText] = useState('');
    const [successMsg, setSuccessMsg] = useState(false);
    const [successMsgText, setSuccessMsgText] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [dateError, setDateError] = useState(false);
    // delete popup
    const [deletePopup, setDeletePopup] = useState(false);
    const [delSelectedId, setDelSelectedId] = useState(null);
    // history
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isSelected, setIsSelected] = useState(false);
    const [isHistoryClicked, setIsHistoryClicked] = useState(false);
    const [historyList, setHistoryList] = useState([]);
    // form fields
    const [pip, setPip] = useState('');
    const [peep, setPeep] = useState('');
    const [fio2, setFio2] = useState('');
    const [vree, setVree] = useState('');
    const [ti, setTi] = useState('');
    const [rr, setRr] = useState('');
    const [pattern_respiratory, setPatternRespiratory] = useState('');
    const [spo2, setSpo2] = useState('');
    const [tv_mv, setTvMv] = useState('');
    const [et_suction, setEtSuction] = useState('');
    const [cxr_finding, setCxrFinding] = useState('');
    const [ph, setPh] = useState('');
    const [pao2, setPao2] = useState('');
    const [pco2, setPco2] = useState('');
    const [hco3, setHco3] = useState('');
    const [be, setBe] = useState('');
    const [remarks, setRemarks] = useState('');

    useEffect(() => {
        const foClinicID = JSON.parse(localGetItem('loggedInUserInfo') || '{}');
        const cId = JSON.parse(localGetItem('SelectedDoctorClinicId')) || foClinicID?.clinic_id;
        setClinicId(cId);
        const patientDetails = getCachevalue('IppatientData');
        const patients = JSON.parse(patientDetails || '{}');
        const appointmentDetail = getCachevalue('withoutAppointment');
        const details = JSON.parse(appointmentDetail || '{}');
        getNurseList(cId);
        if (details?.patient_id === patients?.patient_id) {
            setWithOutAppointment(details);
            setPatientData(patients);
            getPatientData(patients, details, cId);
            getVentilationData(patients, details, cId);
        } else {
            setPatientData(patients);
            getPatientData(patients, {}, cId);
            getVentilationData(patients, {}, cId);
        }
    }, []);

    const errorMsgFunc = (message) => {
        setIsErrorMsg(true);
        setIsErrorMsgText(message);
    };

    const successMsgFunc = (message) => {
        setSuccessMsg(true);
        setSuccessMsgText(message);
    };

    const msgClose = () => {
        setIsErrorMsg(false);
        setIsErrorMsgText('');
        setSuccessMsg(false);
        setSuccessMsgText('');
    };

    const getNurseList = (cId) => {
        try {
            RestAPIService.getAll(Serviceurls.FO_STAFF_LIST + `?clinic_id=${cId}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        const list = response.data.data || [];
                        setNurseList(list);
                        if (list?.length && localGetItem('NurseId')) {
                            const nurseId = JSON.parse(localGetItem('NurseId'));
                            const nurseData = list.find((item) => item.staff_id === nurseId);
                            setNurseName(nurseData || null);
                        }
                    }
                }).catch((error) => {
                    errorMsgFunc(error?.response?.data?.message || error.message);
                });
        } catch (error) {
            errorMsgFunc(error.message);
        }
    };

    const getIpAdmissionId = () => {
        return patientData?.id || withOutAppointment?.ip_admission_id || patientData?.ip_admission_id || (() => { try { const j = JSON.parse(getCachevalue('IppatientData') || 'null'); return j?.id || j?.ip_admission_id } catch { return null } })() || null;
    }

    const getPatientData = (pData, withoutAppt, cId) => {
        const admission_id = pData?.id || withoutAppt?.ip_admission_id || getIpAdmissionId();
        let url = `${(localGetItem('isNewBilling') === 'false' ? Serviceurls.IP_PATIENT_DETAILS : Serviceurls.IP_PATIENT_DETAILS_NEW)}?admission_id=${admission_id}`;
        if (props.is_From_IpNotes && cId) {
            url = `${url}&clinic_id=${cId}`;
        }
        try {
            RestAPIService.getAll(url)
                .then((response) => {
                    if (response.data.status === 'success') {
                        setAttenderData(response.data.data[0]);
                    }
                }).catch((error) => {
                    errorMsgFunc(error?.response?.data?.message || error.message);
                });
        } catch (error) {
            errorMsgFunc(error.message);
        }
    };

    const getVentilationData = (pData, withoutAppt, cId) => {
        const admission_id = pData?.id || withoutAppt?.ip_admission_id;
        setIsLoader(true);
        try {
            RestAPIService.getAll(`${Serviceurls.IP_VENTILATION_CHART}?ip_admission_id=${admission_id}&clinic_id=${cId}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        setVentilationData(response.data.data || []);
                        setIsLoader(false);
                    }
                }).catch((error) => {
                    setIsLoader(false);
                    errorMsgFunc(error?.response?.data?.message || error.message);
                });
        } catch (error) {
            setIsLoader(false);
            errorMsgFunc(error.message);
        }
    };

    const refreshData = () => {
        getVentilationData(patientData, withOutAppointment, clinicId);
    };

    const sortHistoryByModifiedTimeDesc = (list) => {
        return [...(list || [])].sort((a, b) => {
            const ta = a.modified_date_to || a.modified_date_from || '';
            const tb = b.modified_date_to || b.modified_date_from || '';
            return String(tb).localeCompare(String(ta));
        });
    };

    const getHistory = (id) => {
        try {
            RestAPIService.getAll(`${Serviceurls.FO_IP_HISTORY}?condition=ventilation&row_id=${id}&clinic_id=${clinicId}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        setHistoryList(sortHistoryByModifiedTimeDesc(response.data.data || []));
                        setIsHistoryClicked(true);
                    }
                }).catch((error) => {
                    errorMsgFunc(error?.response?.data?.message || error.message);
                });
        } catch (error) {
            errorMsgFunc(error.message);
        }
    };

    const handleSave = () => {
        const hasData = [pip, peep, fio2, vree, ti, rr, pattern_respiratory, spo2,
            tv_mv, et_suction, cxr_finding, ph, pao2, pco2, hco3, be
        ].some(v => v !== '' && v != null);
        if (!hasData) return errorMsgFunc(t('Please, Add Atleast One Value'));
        const admission_id = patientData?.id || withOutAppointment?.ip_admission_id;
        const myUser = JSON.parse(localGetItem('loggedInUserInfo') || '{}');
        const entry_time = DateTime.fromJSDate(ventilationDate ? new Date(ventilationDate) : new Date()).toFormat('yyyy-MM-dd HH:mm:ss');
        const data = {
            ip_admission_id: admission_id,
            patient_id: patientData?.patient_id || withOutAppointment?.patient_id || null,
            nurse_id: nurseName?.staff_id || null,
            doctor_id: attenderData?.doctor_id || myUser?.doctor_id || null,
            entry_time,
            pip: pip || null,
            peep: peep || null,
            fio2: fio2 || null,
            vree: vree || null,
            ti: ti || null,
            rr: rr || null,
            pattern_respiratory: CommonValidation.removeSpace(pattern_respiratory) || null,
            spo2: spo2 || null,
            tv_mv: tv_mv || null,
            et_suction: CommonValidation.removeSpace(et_suction) || null,
            cxr_finding: CommonValidation.removeSpace(cxr_finding) || null,
            ph: ph || null,
            pao2: pao2 || null,
            pco2: pco2 || null,
            hco3: hco3 || null,
            be: be || null,
            remarks: CommonValidation.removeSpace(remarks) || null,
            clinic_id: clinicId
        };
        if (isEdit && selectedId) {
            data.id = selectedId;
        }
        setIsSaving(true);
        try {
            RestAPIService.create(data, Serviceurls.IP_VENTILATION_CHART + `?clinic_id=${clinicId}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        handleClose();
                        refreshData();
                        successMsgFunc(response.data.message);
                    } else {
                        setIsSaving(false);
                        errorMsgFunc(response.data.message);
                    }
                }).catch((error) => {
                    setIsSaving(false);
                    errorMsgFunc(error?.response?.data?.message || error.message);
                });
        } catch (error) {
            setIsSaving(false);
            errorMsgFunc(error.message);
        }
    };

    const popupClose = () => {
        setDeletePopup(false);
        setDelSelectedId(null);
    };

    const handleDelete = (item) => {
        setDeletePopup(true);
        setDelSelectedId(item?.id);
    };

    const confirmDelete = () => {
        try {
            RestAPIService.delete(Serviceurls.IP_VENTILATION_CHART + `?id=${delSelectedId}`)
                .then((response) => {
                    if (response?.data?.status === 'success') {
                        successMsgFunc(response?.data?.message);
                        setDeletePopup(false);
                        setDelSelectedId(null);
                        refreshData();
                    }
                }).catch((error) => {
                    errorMsgFunc(error?.response?.data?.message || error.message);
                });
        } catch (error) {
            errorMsgFunc(error.message);
        }
    };

    const onEditHandler = (item) => {
        const existingNurse = NurseList.find(n => n.staff_id === item.nurse_profile_detail_id);
        const parsedDate = item?.entry_time ? DateTime.fromISO(item.entry_time).toJSDate() : new Date();
        setOpenPopup(true);
        setIsEdit(true);
        setSelectedId(item?.id);
        setVentilationDate(parsedDate);
        setNurseName(existingNurse || null);
        setPip(item?.pip || '');
        setPeep(item?.peep || '');
        setFio2(item?.fio2 || '');
        setVree(item?.vree || '');
        setTi(item?.ti || '');
        setRr(item?.rr || '');
        setPatternRespiratory(item?.pattern_respiratory || '');
        setSpo2(item?.spo2 || '');
        setTvMv(item?.tv_mv || '');
        setEtSuction(item?.et_suction || '');
        setCxrFinding(item?.cxr_finding || '');
        setPh(item?.ph || '');
        setPao2(item?.pao2 || '');
        setPco2(item?.pco2 || '');
        setHco3(item?.hco3 || '');
        setBe(item?.be || '');
        setRemarks(item?.remarks || '');
    };

    const handleClose = () => { handleClear(); setOpenPopup(false); };

    const handleClear = () => {
        setIsEdit(false);
        setSelectedId(null);
        setIsSaving(false);
        setDateError(false);
        setVentilationDate(new Date());
        setPip('');
        setPeep('');
        setFio2('');
        setVree('');
        setTi('');
        setRr('');
        setPatternRespiratory('');
        setSpo2('');
        setTvMv('');
        setEtSuction('');
        setCxrFinding('');
        setPh('');
        setPao2('');
        setPco2('');
        setHco3('');
        setBe('');
        setRemarks('');
        if (NurseList?.length && localGetItem('NurseId')) {
            const nurseId = JSON.parse(localGetItem('NurseId'));
            const nurseData = NurseList.find((item) => item.staff_id === nurseId);
            setNurseName(nurseData || null);
        } else {
            setNurseName(null);
        }
    };

    const historyPopUpClose = () => {
        setIsHistoryClicked(false);
        setHistoryList([]);
    };

    const renderNumericField = (label, value, setter, unit = '') => {
        return (
            <TextField
                size='small'
                label={t(label)}
                value={value}
                onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || CommonValidation.DecimalNumber(val)) {
                        setter(val);
                    }
                }}
                InputProps={unit ? { endAdornment: <InputAdornment position='end'><Typography variant='caption'>{unit}</Typography></InputAdornment> } : {}}
                fullWidth
            />
        );
    };

    const renderTextField = (label, value, setter, maxLen = 200) => {
        return (
            <TextField
                size='small'
                label={t(label)}
                value={value}
                onChange={(e) => { if (e.target.value.trimStart().length <= maxLen) setter(e.target.value.trimStart()); }}
                fullWidth
            />
        );
    };

    const renderMenuItems = (row) => (
        <div>
            <Tooltip title={t('More')} placement='top' arrow>
                <IconButton size='small' onClick={(e) => { setAnchorEl(e.currentTarget); setSelectedRow(row.id); setIsSelected(true); }}>
                    <MoreVertIcon fontSize='small' />
                </IconButton>
            </Tooltip>
            {(isSelected && selectedRow === row.id) ?
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    <MenuItem onClick={() => { setAnchorEl(null); getHistory(row.id); }}>{t('History')}</MenuItem>
                </Menu> : null}
        </div>
    );

    const renderHistoryContent = () => {
        const historyFieldSortValue = (row, key) => {
            const v = row[`${key}_to`] !== undefined && row[`${key}_to`] !== null ? row[`${key}_to`] : row[`${key}_from`];
            if (v === undefined || v === null || v === '') return null;
            if (typeof v === 'number' && !Number.isNaN(v)) return v;
            const n = Number(v);
            const s = String(v).trim();
            if (s !== '' && !Number.isNaN(n) && s === String(n)) return n;
            return String(v);
        };
        const compareHistorySort = (a, b) => {
            if (a == null && b == null) return 0;
            if (a == null) return 1;
            if (b == null) return -1;
            if (typeof a === 'number' && typeof b === 'number') return a - b;
            return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
        };
        const historyFields = [
            { key: 'pip', label: 'PIP', minWidth: 90 },
            { key: 'peep', label: 'PEEP', minWidth: 90 },
            { key: 'fio2', label: 'FiO₂', minWidth: 90 },
            { key: 'vree', label: 'VREE', minWidth: 90 },
            { key: 'ti', label: 'TI', minWidth: 90 },
            { key: 'rr', label: 'RR', minWidth: 90 },
            { key: 'pattern_respiratory', label: 'Pattern Resp.', minWidth: 120 },
            { key: 'spo2', label: 'SpO₂', minWidth: 90 },
            { key: 'tv_mv', label: 'TV/MV', minWidth: 100 },
            { key: 'et_suction', label: 'ET Suction', minWidth: 110 },
            { key: 'cxr_finding', label: 'CXR Finding', minWidth: 120 },
            { key: 'ph', label: 'pH', minWidth: 90 },
            { key: 'pao2', label: 'PaO₂', minWidth: 90 },
            { key: 'pco2', label: 'PCO₂', minWidth: 90 },
            { key: 'hco3', label: 'HCO₃', minWidth: 90 },
            { key: 'be', label: 'BE', minWidth: 90 },
            { key: 'remarks', label: 'Remarks', minWidth: 110 },
        ];
        const rows = (historyList || []).map((item, idx) => ({ ...item, sno: idx + 1 }));
        const fmtVal = (val, iso) => {
            if (val == null) return '-';
            return iso ? convertDateTime(String(val)) : String(val);
        };
        const mkCell = (params, f) => {
            const from = fmtVal(params.row[`${f.key}_from`], f.iso);
            const to = fmtVal(params.row[`${f.key}_to`], f.iso);
            if (from === '-' && to === '-') return <Box component='div'>-</Box>;
            const text = `${from} | ${to}`;
            return (
                <Tooltip placement='top' title={text} arrow>
                    <Box component='div' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</Box>
                </Tooltip>
            );
        };
        const columns = [
            {
                field: 'modified_by_to', headerName: t('Modified By'), minWidth: 130,
                valueGetter: (params) => params.row.modified_by_to || params.row.modified_by_from || '',
                sortComparator: (v1, v2) => String(v1 || '').localeCompare(String(v2 || ''), undefined, { sensitivity: 'base' }),
                renderCell: (params) => <Box component='div'>{params.row.modified_by_to || '-'}</Box>
            },
            {
                field: 'modified_date', headerName: t('Modified Date And Time'), minWidth: 160, headerAlign: 'center', align: 'center',
                valueGetter: (params) => params.row.modified_date_to || params.row.modified_date_from || '',
                sortComparator: (v1, v2) => String(v1 || '').localeCompare(String(v2 || '')),
                renderCell: (params) => <Box component='div'>{params.row.modified_date || '-'}</Box>
            },
            ...historyFields.map(f => ({
                field: f.key,
                headerName: f.label,
                minWidth: f.minWidth,
                valueGetter: (params) => historyFieldSortValue(params.row, f.key),
                sortComparator: (v1, v2) => compareHistorySort(v1, v2),
                renderCell: (params) => mkCell(params, f)
            }))
        ];
        return (
            <Box component='div' className='eMed_history_nurse_grid' style={{ width: '86vw' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.sno}
                    density='compact'
                    hideFooter
                    components={{
                        NoRowsOverlay: () => (
                            <Box className='eMed_conf_nodata'>{t('NoRecordsFound')}</Box>
                        )
                    }}
                />
            </Box>
        );
    };

    const attenderCardData = {
        lineOne: t('AttenderName'),
        lineTwo: `${attenderData?.attender_name || '-'}`,
        lineFour: t('ContactNumber'),
        LineFive: `${attenderData?.attender_mobile || '-'}`
    };
    const Consultant = {
        image: ImagePaths.Doctor_Icon.default,
        width: '2.3vw',
        height: '2.3vw',
        lineOne: t('ConsultantName'),
        lineTwo: `${attenderData?.doctor_name || '-'}`
    };

    const renderCell = (value, limit) => {
        const str = value != null && value !== '' ? String(value) : null;
        const display = str ? (str.length > limit ? str.slice(0, limit) + '...' : str) : '-';
        return <Tooltip placement='top' title={str || ''}><Typography variant='body2'>{display}</Typography></Tooltip>;
    };
    const renderFullCell = (value) => {
        const str = value != null && value !== '' ? String(value) : '-';
        return <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>{str}</Typography>;
    };
    const currentDate = new Date();
    const admissionDate = patientData?.admitted_date ? new Date(patientData.admitted_date)
        : patientData?.admission_date ? new Date(patientData.admission_date) : currentDate;
    const dischargeDate = patientData?.discharge_date && (new Date(patientData.discharge_date) <= currentDate)
        ? new Date(patientData.discharge_date) : currentDate;
    const paginatedRows = ventilationData.slice(page * pageSize, page * pageSize + pageSize);
    const checkuserAccess = props.is_From_IpNotes ? false :
        CheckAccessFunc('front_office', 'In Patients', 'Clinical Notes', 'Ventilation Flow Sheet', 'SubTab')?.editAccess ?
            withOutAppointment?.is_ip : true;

    const thSx = { backgroundColor: Colors.DataTblHeaderbg, fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', py: '7px', px: '14px' };
    const tdSx = { fontSize: '0.85rem', py: '5px', px: '14px' };
    const stickyLTh = { ...thSx, position: 'sticky', left: 0, zIndex: 5 };
    const stickyRTh = { ...thSx, position: 'sticky', right: 0, zIndex: 5 };
    const stickyLTd = { ...tdSx, position: 'sticky', left: 0, zIndex: 1, backgroundColor: Colors.white, 'tr:hover &': { backgroundColor: '#f5f5f5' } };
    const stickyRTd = { ...tdSx, position: 'sticky', right: 0, zIndex: 1, backgroundColor: Colors.white, 'tr:hover &': { backgroundColor: '#f5f5f5' } };

    return (
        <Box sx={{ backgroundColor: Colors.configHomeBg, height: '74dvh', p: '0.625vw' }}>
            {/* Patient info header */}
            <Grid container>
                <Stack className='eMed_medicationCard_header' direction='row' justifyContent='space-between' alignItems='center' sx={{ width: '100%' }}>
                    <Stack direction='row' justifyContent='space-evenly' alignItems='center' spacing={1.2}>
                        <Stack className='eMed_recCard_div' sx={{ width: '23vw', backgroundColor: Colors.white }}>
                            <CommonPatientDetails data={patientData} showDetailed={true} />
                        </Stack>
                        <CommonPatientCard details={attenderCardData} showDetailed={true} />
                        <ConsultantPatientCard name={Consultant} width={'20dvw'} />
                    </Stack>
                    <Button
                        variant='contained'
                        sx={{ textTransform: 'capitalize', height: '4.5dvh', width: '10dvw' }}
                        disabled={checkuserAccess}
                        onClick={() => setOpenPopup(true)}
                    >
                        {t('Add')}
                    </Button>
                </Stack>
            </Grid>

            {/* Table area */}
            <Box sx={{ height: '65dvh', display: 'flex', flexDirection: 'column', backgroundColor: Colors.white }}>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                    <Table stickyHeader size='small' sx={{ width: 'max-content', minWidth: '100%' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={stickyLTh}>Date & Time</TableCell>
                                <TableCell sx={thSx}>PIP (cmH₂O)</TableCell>
                                <TableCell sx={thSx}>PEEP (cmH₂O)</TableCell>
                                <TableCell sx={thSx}>FiO₂ (%)</TableCell>
                                <TableCell sx={thSx}>VREE (ml)</TableCell>
                                <TableCell sx={thSx}>TI (sec)</TableCell>
                                <TableCell sx={thSx}>RR (breaths/min)</TableCell>
                                <TableCell sx={thSx}>Pattern Respiratory</TableCell>
                                <TableCell sx={thSx}>SpO₂ (%)</TableCell>
                                <TableCell sx={thSx}>TV/MV (L/min)</TableCell>
                                <TableCell sx={thSx}>ET Suction</TableCell>
                                <TableCell sx={thSx}>CXR Finding</TableCell>
                                <TableCell sx={thSx}>pH</TableCell>
                                <TableCell sx={thSx}>PaO₂ (mmHg)</TableCell>
                                <TableCell sx={thSx}>PCO₂ (mmHg)</TableCell>
                                <TableCell sx={thSx}>HCO₃ (mEq/L)</TableCell>
                                <TableCell sx={thSx}>BE (mEq/L)</TableCell>
                                <TableCell sx={thSx}>Remarks</TableCell>
                                <TableCell sx={stickyRTh} align='center'>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={19} align='center' sx={{ py: 3 }}>{t('NoRecordsFound')}</TableCell>
                                </TableRow>
                            ) : paginatedRows.map((row, i) => (
                                <TableRow key={row.id || i} hover>
                                    <TableCell sx={stickyLTd}>{renderFullCell(row.entry_time ? convertDateTime(row.entry_time) : null)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.pip)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.peep)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.fio2)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.vree)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.ti)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.rr)}</TableCell>
                                    <TableCell sx={tdSx}>{renderCell(row.pattern_respiratory, 15)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.spo2)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.tv_mv)}</TableCell>
                                    <TableCell sx={tdSx}>{renderCell(row.et_suction, 10)}</TableCell>
                                    <TableCell sx={tdSx}>{renderCell(row.cxr_finding, 10)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.ph)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.pao2)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.pco2)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.hco3)}</TableCell>
                                    <TableCell sx={tdSx}>{renderFullCell(row.be)}</TableCell>
                                    <TableCell sx={tdSx}>{renderCell(row.remarks, 8)}</TableCell>
                                    <TableCell sx={stickyRTd} align='center'>
                                        <Box display='flex' alignItems='center' justifyContent='center'>
                                            <CommonEditButton size='1.5vw' disable={checkuserAccess} onClick={() => onEditHandler(row)} />
                                            <CommonDeleteButton size='1.5vw' disable={checkuserAccess} onClick={() => handleDelete(row)} />
                                            {renderMenuItems(row)}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 20, 30]}
                    component='div'
                    count={ventilationData.length}
                    rowsPerPage={pageSize}
                    page={page}
                    onPageChange={(_e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Box>

            {/* Add/Edit Dialog */}
            <Dialog open={openPopup} maxWidth='md' fullWidth>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '80vh' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '1rem', py: '0.5rem', backgroundColor: Colors.DataTblHeaderbg }}>
                        <Typography fontWeight={'600'} fontSize={'0.95rem'}>{t('Ventilation Flow Sheet')}</Typography>
                        <IconButton size='small' onClick={() => handleClose()}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto', px: '1rem', py: '0.75rem' }}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={6}>{renderNumericField('PIP', pip, setPip, 'cmH₂O')}</Grid>
                            <Grid item xs={6}>{renderNumericField('PEEP', peep, setPeep, 'cmH₂O')}</Grid>
                            <Grid item xs={6}>{renderNumericField('FiO₂', fio2, setFio2, '%')}</Grid>
                            <Grid item xs={6}>{renderNumericField('VREE', vree, setVree, 'ml')}</Grid>
                            <Grid item xs={6}>{renderNumericField('TI', ti, setTi, 'sec')}</Grid>
                            <Grid item xs={6}>{renderNumericField('RR', rr, setRr, 'breaths/min')}</Grid>
                            <Grid item xs={6}>{renderTextField('Pattern Respiratory', pattern_respiratory, setPatternRespiratory, 30)}</Grid>
                            <Grid item xs={6}>{renderNumericField('SpO₂', spo2, setSpo2, '%')}</Grid>
                            <Grid item xs={6}>{renderNumericField('TV/MV', tv_mv, setTvMv, 'L/min')}</Grid>
                            <Grid item xs={6}>{renderTextField('ET Suction', et_suction, setEtSuction, 30)}</Grid>
                            <Grid item xs={6}>{renderTextField('CXR Finding', cxr_finding, setCxrFinding, 30)}</Grid>
                            <Grid item xs={6}>{renderNumericField('pH', ph, setPh, '')}</Grid>
                            <Grid item xs={6}>{renderNumericField('PaO₂', pao2, setPao2, 'mmHg')}</Grid>
                            <Grid item xs={6}>{renderNumericField('PCO₂', pco2, setPco2, 'mmHg')}</Grid>
                            <Grid item xs={6}>{renderNumericField('HCO₃', hco3, setHco3, 'mEq/L')}</Grid>
                            <Grid item xs={6}>{renderNumericField('BE', be, setBe, 'mEq/L')}</Grid>
                            <Grid item xs={6}>
                                <Autocomplete
                                    size='small'
                                    options={NurseList || []}
                                    getOptionLabel={(option) => option.nurse || ''}
                                    value={nurseName}
                                    onChange={(_e, val) => setNurseName(val)}
                                    renderInput={(params) => <TextField {...params} label={t('Nurse Name')} />}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6} />
                            <Grid item xs={6}>
                                <TextField size='small' label={t('Remarks')} value={remarks} multiline rows={3} fullWidth
                                    onChange={(e) => { if (e.target.value.trimStart().length <= 100) setRemarks(e.target.value.trimStart()); }} />
                            </Grid>
                            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DateTimePicker
                                        inputFormat='DD-MM-YYYY & hh:mm A'
                                        value={ventilationDate}
                                        minDate={admissionDate}
                                        maxDate={dischargeDate}
                                        maxDateTime={dischargeDate}
                                        disableFuture
                                        onError={(err) => setDateError(!!err)}
                                        onChange={(newVal) => setVentilationDate(newVal === null ? new Date() : newVal)}
                                        renderInput={(params) => (
                                            <TextField size='small' {...params} variant='standard' fullWidth />
                                        )}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, px: '1rem', py: '0.75rem', backgroundColor: Colors.DataTblHeaderbg, borderTop: `1px solid ${Colors.lightGray || '#e0e0e0'}` }}>
                        <Button variant='outlined' size='small' id='eMed_recPayment_btn' onClick={() => handleClear()}>
                            {t('Clear')}
                        </Button>
                        <Button
                            variant='contained'
                            size='small'
                            id='eMed_recPayment_btn'
                            disabled={isSaving || dateError}
                            onClick={() => { handleSave(); }}
                        >
                            {t('Add')}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {deletePopup &&
                <DeletePopup
                    DeletTitle={`${t('deleteMsg')} ?`}
                    deleteAlertPopupClose={popupClose}
                    removeData={confirmDelete}
                />
            }
            {isHistoryClicked &&
                <CommonPopUp
                    width={'90vw'}
                    backgroundColor={Colors.white}
                    popUpClose={historyPopUpClose}
                    title={t('History')}
                    component={renderHistoryContent}
                />
            }

            {isErrorMsg && (
                <ToastMsg severity='error' msg={isErrorMsgText} msgPop={msgClose} />
            )}
            {successMsg && (
                <ToastMsg severity='success' msg={successMsgText} msgPop={msgClose} />
            )}
            <Loader loaderOpen={isLoader} />
        </Box>
    );
};

export default VentilationFlowSheet;
